/**
 * Owns Vehicle Map GPS replay state and reports the live position + travelled trail.
 */

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import GpsPlaybackScrubber from './GpsPlaybackScrubber';
import {
  buildOrderJourneyPlaybackTrack,
  buildVehicleDayPlaybackTrack,
  positionAtTimestamp,
  trailCoordinatesUpToTimestamp,
  withOrderProximityEvents,
  type OrderDeliveryProximityInput,
  type OrderHandoffProximityInput,
} from '../../utils/vehicleDayPlaybackTrack';

type StopInput = {
  stop_number: number;
  arrival_time?: string;
  departure_time?: string;
  latitude?: number | null;
  longitude?: number | null;
  stop_type?: string;
  postcode?: string;
};

type SegmentInput = {
  from_stop_number: number;
  to_stop_number: number;
  segment_start?: string;
  segment_end?: string;
  points?: [number, number][];
  coordinates?: [number, number][];
  label?: string | null;
  vehicle?: string | null;
};

export type MapPlaybackUpdate = {
  lat: number;
  lon: number;
  timestamp: string;
  /** Travelled path as MapLibre [lon, lat][] */
  trail: [number, number][];
} | null;

export interface VehicleMapGpsPlaybackProps {
  stops: StopInput[];
  segments: SegmentInput[];
  /** Reset key when modal vehicle/day/order changes */
  resetKey: string;
  onPositionChange: (pos: MapPlaybackUpdate) => void;
  /** Amber scrubber marks — first GPS approach to customer delivery pins */
  orderDeliveries?: OrderDeliveryProximityInput[];
  /** Cyan scrubber marks — first GPS approach to hub handoff pins */
  orderHandoffs?: OrderHandoffProximityInput[];
  /**
   * Order map: play Trunk then Delivery in journey order (do not merge-sort
   * two vehicles' wall-clock GPS — that fights and late-shifts delivery).
   */
  orderJourneyMode?: boolean;
}

/** Throttle parent map trail updates so modal re-renders stay cheap while playing. */
const TRAIL_EMIT_MIN_MS = 200;

function proximityKey(
  deliveries: OrderDeliveryProximityInput[] | undefined,
  handoffs: OrderHandoffProximityInput[] | undefined,
): string {
  const d = (deliveries ?? [])
    .map((x) => `d:${x.orderId}:${x.lat.toFixed(5)},${x.lon.toFixed(5)}`)
    .sort()
    .join('|');
  const h = (handoffs ?? [])
    .map((x) => `h:${x.orderId}:${x.lat.toFixed(5)},${x.lon.toFixed(5)}`)
    .sort()
    .join('|');
  return `${d}#${h}`;
}

function segmentsFingerprint(segments: SegmentInput[]): string {
  return segments
    .map((s) => {
      const n = Array.isArray(s.points) ? s.points.length : Array.isArray(s.coordinates) ? s.coordinates.length : 0;
      return `${s.label ?? ''}:${s.vehicle ?? ''}:${s.segment_start ?? ''}:${s.segment_end ?? ''}:${n}`;
    })
    .join('|');
}

export default function VehicleMapGpsPlayback({
  stops,
  segments,
  resetKey,
  onPositionChange,
  orderDeliveries,
  orderHandoffs,
  orderJourneyMode = false,
}: VehicleMapGpsPlaybackProps) {
  const proxKey = proximityKey(orderDeliveries, orderHandoffs);
  const segKey = segmentsFingerprint(segments);

  const track = useMemo(() => {
    const base = orderJourneyMode
      ? buildOrderJourneyPlaybackTrack(segments)
      : buildVehicleDayPlaybackTrack(stops, segments);
    if (!base) return null;
    return withOrderProximityEvents(base, {
      deliveries: orderDeliveries ?? [],
      handoffs: orderHandoffs ?? [],
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps -- rebuild when day/order geometry or pins change
  }, [resetKey, proxKey, segKey, orderJourneyMode]);

  const [currentTimestamp, setCurrentTimestamp] = useState<string | null>(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [playbackSpeed, setPlaybackSpeed] = useState(5);
  const lastEmitMs = useRef(0);
  const pendingEmit = useRef<ReturnType<typeof setTimeout> | null>(null);
  const latestUpdate = useRef<MapPlaybackUpdate>(null);
  const onPositionChangeRef = useRef(onPositionChange);
  onPositionChangeRef.current = onPositionChange;

  useEffect(() => {
    setCurrentTimestamp(null);
    setIsPlaying(false);
    setPlaybackSpeed(5);
    lastEmitMs.current = 0;
    latestUpdate.current = null;
    if (pendingEmit.current) {
      clearTimeout(pendingEmit.current);
      pendingEmit.current = null;
    }
    onPositionChangeRef.current(null);
  }, [resetKey, proxKey, segKey, orderJourneyMode]);

  const emitPosition = useCallback((update: MapPlaybackUpdate, force = false) => {
    latestUpdate.current = update;
    const now = Date.now();
    const flush = () => {
      lastEmitMs.current = Date.now();
      pendingEmit.current = null;
      onPositionChangeRef.current(latestUpdate.current);
    };

    if (force || update == null || now - lastEmitMs.current >= TRAIL_EMIT_MIN_MS) {
      if (pendingEmit.current) {
        clearTimeout(pendingEmit.current);
        pendingEmit.current = null;
      }
      flush();
      return;
    }

    if (pendingEmit.current) return;
    pendingEmit.current = setTimeout(flush, TRAIL_EMIT_MIN_MS - (now - lastEmitMs.current));
  }, []);

  useEffect(() => {
    if (!track || !currentTimestamp) {
      emitPosition(null, true);
      return;
    }
    const pos = positionAtTimestamp(track.points, currentTimestamp);
    if (!pos) {
      emitPosition(null, true);
      return;
    }
    const trail = trailCoordinatesUpToTimestamp(track.points, currentTimestamp);
    emitPosition({ ...pos, timestamp: currentTimestamp, trail });
  }, [track, currentTimestamp, emitPosition]);

  useEffect(() => () => {
    if (pendingEmit.current) clearTimeout(pendingEmit.current);
  }, []);

  const onPlaybackControlsChange = useCallback(
    (playing: boolean, speed: number, timestamp: string | null) => {
      setIsPlaying(playing);
      setPlaybackSpeed(speed);
      setCurrentTimestamp(timestamp);
    },
    [],
  );

  if (!track) {
    return (
      <div className="rounded-lg border border-border/70 bg-popover/85 backdrop-blur-sm px-3 py-2 text-xs text-muted-foreground shadow-lg">
        GPS replay unavailable — no timed stops or route segments for this day
      </div>
    );
  }

  return (
    <GpsPlaybackScrubber
      fromIso={track.fromIso}
      toIso={track.toIso}
      currentTimestamp={currentTimestamp}
      isPlaying={isPlaying}
      playbackSpeed={playbackSpeed}
      events={track.events}
      onPlaybackControlsChange={onPlaybackControlsChange}
    />
  );
}
